import React, { useEffect, useRef } from 'react'
import { COLORS } from 'tokens'
import AppointmentCard from '../../../../components/AppointmentCard/AppointmentCard'
import { useShopPatientIds } from '../../../../hooks/useShopPatientIds'
import {
  callInNextVisitAsync,
  setPriorityVisit,
  resetPriorityVisit,
  lobbyUndoVisitAsync,
} from '../../../../redux/flows/portal/lobby'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { APIStatus } from '../../../../redux/types'
import { shouldShowGreenBg } from '../../../../utils/shouldShowGreenBg'
import { getVisitBadges } from '../getVisitBadges'
import { TimeSection, Label, ColumnCard, ColumnHeader, Title, Count, ColumnContent } from '../index.styled'

export default function CheckedInPanel({ data }: { data: { ontime: any[]; late: any[] } }) {
  const dispatch = useAppDispatch()
  const shopPatientIds = useShopPatientIds()
  const queue = useAppSelector((state) => state.lobbyPortal.queue)
  const callInNextVisitStatus = useAppSelector((state) => state.lobbyPortal.callInNextVisitStatus)
  const priorityVisit = useAppSelector((state) => state.lobbyPortal.priorityVisit)
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)

  const handlePatientClick = (item: any, index: number) => {
    if (callInNextVisitStatus === APIStatus.pending) return

    // If clicking on the already-selected priority visit, deselect it
    if (priorityVisit?.id === item.visitId) {
      dispatch(resetPriorityVisit())
      return
    }

    // Only set priority for non-first items
    if (index !== 0) {
      const priorityVisit = {
        id: item.visitId,
        name: item.visit?.patient?.user?.firstname || '',
      }
      dispatch(setPriorityVisit(priorityVisit))
    }
  }

  const handleLongPressStart = (item: any) => {
    // Clear any existing timeout first to prevent race conditions
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }

    longPressTimerRef.current = setTimeout(() => {
      dispatch(lobbyUndoVisitAsync({ visitId: item.visitId }))
      longPressTimerRef.current = null
    }, 2000) // 2 seconds for long press
  }

  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }

  // Cleanup: clear timeout on unmount to prevent accidental triggers
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
    }
  }, [])

  return (
    <ColumnCard>
      <ColumnHeader>
        <Title>Checked-In</Title>
        <Count>{data.ontime.length + data.late.length}</Count>
      </ColumnHeader>
      <ColumnContent>
        {data.ontime.map((d, index) => {
          const globalIndex = index
          const isPriorityVisit = priorityVisit?.id === d.visitId
          return (
            <TimeSection key={d.visitId}>
              {d.showTimeStamp && (
                <Label style={{ marginTop: index !== 0 ? '16px' : '' }}>
                  {d.visit?.appointmentTime ? d.timeStamp : '-'}
                </Label>
              )}

              <AppointmentCard
                name={`${d.visit?.patient?.user?.firstname || ''} ${d.visit?.patient?.user?.lastname || ''}`.trim()}
                avatar={d.visit?.patient?.user?.profilePic || undefined}
                badges={getVisitBadges(d, {
                  isUpcomingOrCheckedIn: true,
                  hasShopOrder: shopPatientIds.has(d.visit?.patientId),
                })}
                onClick={() => handlePatientClick(d, globalIndex)}
                onMouseDown={() => handleLongPressStart(d)}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                onTouchStart={() => handleLongPressStart(d)}
                onTouchEnd={handleLongPressEnd}
                cardStyle={{
                  cursor: 'pointer',
                  outline: isPriorityVisit ? '1px solid #000000' : 'none',
                  outlineOffset: '-1px',
                  color: shouldShowGreenBg(d) ? COLORS.textSecondary : '#000000',
                  backgroundColor: shouldShowGreenBg(d) ? COLORS.primary : '',
                  marginTop: !d.showTimeStamp ? '8px' : '',
                }}
                nameStyle={{
                  color: shouldShowGreenBg(d) ? COLORS.textSecondary : '#000000',
                }}
              />
            </TimeSection>
          )
        })}
        {data.late.length > 0 && <Label style={{ marginTop: '16px', marginBottom: '8px' }}>Late</Label>}
        {data.late.map((d, index) => {
          const globalIndex = data.ontime.length + index
          const isPriorityVisit = priorityVisit?.id === d.visitId
          return (
            <AppointmentCard
              name={`${d.visit?.patient?.user?.firstname || ''} ${d.visit?.patient?.user?.lastname || ''}`.trim()}
              avatar={d.visit?.patient?.user?.profilePic || undefined}
              badges={getVisitBadges(d, {
                isUpcomingOrCheckedIn: true,
                hasShopOrder: shopPatientIds.has(d.visit?.patientId),
              })}
              onClick={() => handlePatientClick(d, globalIndex)}
              onMouseDown={() => handleLongPressStart(d)}
              onMouseUp={handleLongPressEnd}
              onMouseLeave={handleLongPressEnd}
              onTouchStart={() => handleLongPressStart(d)}
              onTouchEnd={handleLongPressEnd}
              cardStyle={{
                cursor: 'pointer',
                outline: isPriorityVisit ? '1px solid #000000' : 'none',
                outlineOffset: '-1px',
                marginBottom: 8,
                backgroundColor: shouldShowGreenBg(d) ? COLORS.primary : '',
                color: shouldShowGreenBg(d) ? COLORS.textSecondary : '#000000',
                marginTop: '8px',
              }}
              nameStyle={{
                color: shouldShowGreenBg(d) ? COLORS.textSecondary : '#000000',
              }}
            />
          )
        })}
      </ColumnContent>
    </ColumnCard>
  )
}
